import React, { forwardRef, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Rnd } from 'react-rnd';

/* ── Theme config (must match backend) ── */
const THEME_COLORS = {
  temple_gold: { primary: '#B8860B', secondary: '#FFD700', accent: '#8B0000', bgStart: '#1A1A2E', bgEnd: '#2D1B00', text: '#FAEBD7', border: '#B8860B' },
  sacred_red: { primary: '#DC143C', secondary: '#FF6B6B', accent: '#FFD700', bgStart: '#1A1A2E', bgEnd: '#2D0000', text: '#FFF0F0', border: '#DC143C' },
  nature_green: { primary: '#166534', secondary: '#4ADE80', accent: '#15803D', bgStart: '#0A1A0A', bgEnd: '#1A2E1A', text: '#F0FFF4', border: '#166534' },
  royal_blue: { primary: '#1E3A5F', secondary: '#3B82F6', accent: '#1D4ED8', bgStart: '#0A0A2E', bgEnd: '#1A1A3E', text: '#EFF6FF', border: '#3B82F6' },
  festive_orange: { primary: '#C2410C', secondary: '#F97316', accent: '#FB923C', bgStart: '#1A1008', bgEnd: '#2E1A00', text: '#FFF7ED', border: '#F97316' },
  modern_purple: { primary: '#581C87', secondary: '#A855F7', accent: '#7C3AED', bgStart: '#1A0A2E', bgEnd: '#2E1A3E', text: '#FAF5FF', border: '#A855F7' },
  // Light themes
  cream_classic: { primary: '#8B4513', secondary: '#D2691E', accent: '#A0522D', bgStart: '#FFF8F0', bgEnd: '#FAEBD7', text: '#3E2723', border: '#D2691E' },
  white_elegant: { primary: '#1a1a1a', secondary: '#333333', accent: '#555555', bgStart: '#FFFFFF', bgEnd: '#F5F5F5', text: '#1a1a1a', border: '#999999' },
  sky_light: { primary: '#1E40AF', secondary: '#2563EB', accent: '#3B82F6', bgStart: '#EFF6FF', bgEnd: '#DBEAFE', text: '#1E3A5F', border: '#3B82F6' },
};

/**
 * All font sizes are based on a reference width of 1080px.
 * For larger canvases (A4=2480, etc.), we normalize to the reference
 * so text stays the same visual proportion.
 */
const REF_W = 1080;

/**
 * Auto-scale content to fit within the canvas height.
 */
function useContentScale(formData, refW, canvasH) {
  return useMemo(() => {
    const scheduleCount = (formData.schedule || []).filter(s => s.label).length;
    const pad = refW * 0.04;

    let needed = pad * 2 + 8;
    if (formData.logo_url) needed += refW * 0.11 + refW * 0.015;
    if (formData.org_name) needed += refW * 0.055;
    if (formData.org_name_secondary) needed += refW * 0.035;
    if (formData.org_name || formData.org_name_secondary) needed += refW * 0.03;
    if (formData.event_title) needed += refW * 0.07;
    if (formData.event_subtitle) needed += refW * 0.035;
    if (formData.date) needed += refW * 0.06;
    if (formData.time) needed += refW * 0.03;
    if (scheduleCount > 0) {
      needed += refW * 0.025;
      needed += refW * 0.035;
      needed += scheduleCount * (refW * 0.038);
    }
    if (formData.venue_name) {
      needed += refW * 0.025;
      needed += refW * 0.04;
      needed += refW * 0.05;
      if (formData.venue_address) needed += refW * 0.03;
    }
    if (formData.contact_info) needed += refW * 0.035;
    if (formData.footer_message) needed += refW * 0.05;

    // Scale content up on taller canvases (A4/Portrait/Story) so the poster
    // doesn't leave huge empty space, and down on cramped canvases so content
    // doesn't overflow. Multiplier of 0.85 reserves ~15% margin.
    const fillScale = (canvasH / needed) * 0.85;
    return Math.max(0.6, Math.min(1.8, fillScale));
  }, [formData, refW, canvasH]);
}

const PosterCanvas = forwardRef(function PosterCanvas(
  { formData, freeFormElements, selectedElementId, onSelectElement, onUpdateElement, zoom, format },
  ref
) {
  const themeKey = formData.theme || 'temple_gold';
  const theme = THEME_COLORS[themeKey] || THEME_COLORS.temple_gold;
  // If user picked a custom bg color, override the gradient
  const customBg = formData.bg_color || null;

  const canvasW = format.w;
  const canvasH = format.h;
  const scale = zoom;

  // Normalize font sizes to reference width so A4 (2480px) and Square (1080px) look the same
  const sizeRatio = canvasW / REF_W;
  const contentScale = useContentScale(formData, REF_W, canvasH / sizeRatio);
  // s() converts a REF_W-based size to actual canvas pixels, with content-fit scaling
  const s = (refVal) => refVal * sizeRatio * contentScale;

  const [editingTextId, setEditingTextId] = useState(null);
  const editInputRef = useRef(null);

  useEffect(() => {
    if (editingTextId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingTextId]);

  const handleCanvasClick = useCallback((e) => {
    if (e.target === e.currentTarget || e.target.closest('[data-poster-content]')) {
      onSelectElement(null);
      setEditingTextId(null);
    }
  }, [onSelectElement]);

  const handleTextDoubleClick = useCallback((id) => {
    setEditingTextId(id);
  }, []);

  const handleTextEditDone = useCallback((id, value) => {
    onUpdateElement(id, { content: value });
    setEditingTextId(null);
  }, [onUpdateElement]);

  const filteredSchedule = (formData.schedule || []).filter(si => si.label);

  // Determine background
  let bgStyle;
  if (formData.ai_background_url) {
    bgStyle = `url(${formData.ai_background_url}) center/cover no-repeat`;
  } else if (customBg) {
    bgStyle = customBg;
  } else {
    bgStyle = `linear-gradient(180deg, ${theme.bgStart}, ${theme.bgEnd})`;
  }

  return (
    <div
      style={{ width: canvasW * scale, height: canvasH * scale, flexShrink: 0 }}
      className="relative shadow-2xl"
      data-testid="poster-canvas-wrapper"
    >
      <div
        ref={ref}
        style={{
          width: canvasW, height: canvasH,
          transform: `scale(${scale})`, transformOrigin: 'top left',
          background: bgStyle,
          position: 'relative', overflow: 'hidden',
          fontFamily: "'Noto Sans', 'Noto Sans Tamil', sans-serif",
        }}
        onClick={handleCanvasClick}
        data-testid="poster-canvas"
      >
        {/* Overlay for AI backgrounds */}
        {formData.ai_background_url && (
          <div style={{
            position: 'absolute', inset: 0,
            background: `linear-gradient(180deg, ${theme.bgStart}AA, ${theme.bgEnd}CC)`,
            zIndex: 1,
          }} />
        )}

        {/* Template content */}
        <div data-poster-content style={{
          position: 'relative', zIndex: 2,
          width: '100%', height: '100%',
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center',
          padding: `${s(REF_W * 0.035)}px ${s(REF_W * 0.05)}px`,
          gap: s(REF_W * 0.006),
        }}>

          {/* Top border */}
          <div style={{
            width: '100%', height: Math.max(3, s(4)), borderRadius: 2, flexShrink: 0,
            background: `linear-gradient(90deg, transparent, ${theme.secondary}, transparent)`,
          }} />

          {/* Logo */}
          {formData.logo_url && (
            <div style={{
              width: s(REF_W * 0.1), height: s(REF_W * 0.1), flexShrink: 0,
              borderRadius: '50%', overflow: 'hidden',
              border: `${Math.max(2, s(3))}px solid ${theme.secondary}`,
              background: theme.bgStart,
            }}>
              <img src={formData.logo_url} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
          )}

          {/* Org Name */}
          {formData.org_name && (
            <div style={{
              fontSize: s(REF_W * 0.042), fontWeight: 700, flexShrink: 0,
              color: theme.text, textAlign: 'center',
              letterSpacing: '0.02em', lineHeight: 1.2,
              textShadow: '0 2px 8px rgba(0,0,0,0.4)',
            }}>
              {formData.org_name}
            </div>
          )}

          {/* Secondary Name */}
          {formData.org_name_secondary && (
            <div style={{
              fontSize: s(REF_W * 0.025), color: theme.secondary, flexShrink: 0,
              textAlign: 'center', opacity: 0.9, lineHeight: 1.2,
            }}>
              {formData.org_name_secondary}
            </div>
          )}

          {/* Divider */}
          {(formData.org_name || formData.org_name_secondary) && (
            <div style={{
              width: s(REF_W * 0.25), height: 2, borderRadius: 1, flexShrink: 0,
              background: `linear-gradient(90deg, transparent, ${theme.border}, transparent)`,
            }} />
          )}

          {/* Event Title */}
          {formData.event_title && (
            <div style={{
              fontSize: s(REF_W * 0.05), fontWeight: 800, flexShrink: 0,
              color: theme.secondary, textAlign: 'center',
              lineHeight: 1.15, maxWidth: '90%',
              textShadow: `0 2px 12px ${theme.primary}60`,
            }}>
              {formData.event_title}
            </div>
          )}

          {/* Subtitle */}
          {formData.event_subtitle && (
            <div style={{
              fontSize: s(REF_W * 0.025), color: theme.text, flexShrink: 0,
              textAlign: 'center', opacity: 0.85, lineHeight: 1.2,
            }}>
              {formData.event_subtitle}
            </div>
          )}

          {/* Date Badge */}
          {formData.date && (
            <div style={{
              marginTop: s(REF_W * 0.008), flexShrink: 0,
              padding: `${s(REF_W * 0.01)}px ${s(REF_W * 0.045)}px`,
              borderRadius: s(REF_W * 0.04),
              background: `linear-gradient(135deg, ${theme.accent || theme.primary}, ${theme.primary})`,
              color: '#FFFFFF', fontSize: s(REF_W * 0.028),
              fontWeight: 700, textAlign: 'center',
              boxShadow: `0 4px 16px ${theme.primary}50`,
            }}>
              {formData.date}
            </div>
          )}

          {/* Time */}
          {formData.time && (
            <div style={{
              fontSize: s(REF_W * 0.02), color: theme.text, flexShrink: 0, opacity: 0.8,
            }}>
              {formData.time}
            </div>
          )}

          {/* Schedule */}
          {filteredSchedule.length > 0 && (
            <div style={{
              marginTop: s(REF_W * 0.008), flexShrink: 0,
              width: '72%',
              background: `${theme.bgStart}88`,
              borderRadius: s(REF_W * 0.01),
              border: `1px solid ${theme.border}40`,
              padding: `${s(REF_W * 0.01)}px ${s(REF_W * 0.018)}px`,
              backdropFilter: 'blur(8px)',
            }}>
              {filteredSchedule.map((item, idx) => (
                <div key={idx} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: `${s(REF_W * 0.005)}px 0`,
                  borderBottom: idx < filteredSchedule.length - 1 ? `1px solid ${theme.border}20` : 'none',
                }}>
                  <span style={{ fontSize: s(REF_W * 0.021), color: theme.text }}>{item.label}</span>
                  <span style={{ fontSize: s(REF_W * 0.021), color: theme.secondary, fontWeight: 600 }}>{item.time}</span>
                </div>
              ))}
            </div>
          )}

          {/* Venue */}
          {formData.venue_name && (
            <div style={{
              marginTop: s(REF_W * 0.008), flexShrink: 0,
              width: '72%',
              background: `${theme.primary}20`,
              borderRadius: s(REF_W * 0.01),
              border: `1px solid ${theme.border}30`,
              padding: `${s(REF_W * 0.01)}px ${s(REF_W * 0.018)}px`,
              textAlign: 'center',
            }}>
              <div style={{ fontSize: s(REF_W * 0.015), color: theme.secondary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: s(REF_W * 0.003) }}>
                Venue
              </div>
              <div style={{ fontSize: s(REF_W * 0.025), color: theme.text, fontWeight: 600 }}>{formData.venue_name}</div>
              {formData.venue_address && (
                <div style={{ fontSize: s(REF_W * 0.017), color: theme.text, opacity: 0.75, marginTop: s(REF_W * 0.002) }}>{formData.venue_address}</div>
              )}
            </div>
          )}

          {/* Contact */}
          {formData.contact_info && (
            <div style={{
              fontSize: s(REF_W * 0.017), color: theme.text, opacity: 0.7, flexShrink: 0,
              textAlign: 'center',
            }}>
              {formData.contact_info}
            </div>
          )}

          {/* Footer */}
          {formData.footer_message && (
            <div style={{
              marginTop: 'auto', flexShrink: 0,
              paddingTop: s(REF_W * 0.008),
              fontSize: s(REF_W * 0.025), fontWeight: 600,
              color: theme.secondary, textAlign: 'center', opacity: 0.9,
            }}>
              {formData.footer_message}
            </div>
          )}

          {/* Bottom border */}
          <div style={{
            width: '100%', height: Math.max(3, s(4)), borderRadius: 2, flexShrink: 0,
            background: `linear-gradient(90deg, transparent, ${theme.secondary}, transparent)`,
          }} />
        </div>

        {/* Free-form draggable elements */}
        <div style={{ position: 'absolute', inset: 0, zIndex: 5, pointerEvents: 'none' }}>
          {freeFormElements.map(el => (
            <Rnd
              key={el.id}
              size={{ width: el.width, height: el.height }}
              position={{ x: el.x, y: el.y }}
              onDragStop={(e, d) => onUpdateElement(el.id, { x: d.x, y: d.y })}
              onResizeStop={(e, direction, refEl, delta, position) => {
                onUpdateElement(el.id, {
                  width: parseInt(refEl.style.width),
                  height: parseInt(refEl.style.height),
                  x: position.x, y: position.y,
                });
              }}
              onClick={(e) => { e.stopPropagation(); onSelectElement(el.id); }}
              onDoubleClick={() => el.type === 'text' && handleTextDoubleClick(el.id)}
              bounds="parent"
              scale={scale}
              style={{
                pointerEvents: 'auto',
                outline: selectedElementId === el.id ? '2px solid #3B82F6' : '1px dashed transparent',
                borderRadius: 4, cursor: 'move',
                zIndex: el.zIndex || 10,
                opacity: el.opacity ?? 1,
              }}
              data-testid={`rnd-element-${el.id}`}
            >
              {el.type === 'image' ? (
                <img
                  src={el.url || el.content} alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none', userSelect: 'none' }}
                  draggable={false}
                />
              ) : editingTextId === el.id ? (
                <textarea
                  ref={editInputRef}
                  defaultValue={el.content}
                  onBlur={(e) => handleTextEditDone(el.id, e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Escape') handleTextEditDone(el.id, e.target.value); }}
                  style={{
                    width: '100%', height: '100%',
                    fontSize: el.fontSize || 28,
                    color: el.fontColor || '#FFFFFF',
                    fontFamily: el.fontFamily || 'sans-serif',
                    background: 'rgba(0,0,0,0.3)',
                    border: 'none', outline: 'none',
                    resize: 'none', padding: 4,
                  }}
                />
              ) : (
                <div style={{
                  width: '100%', height: '100%',
                  fontSize: el.fontSize || 28,
                  color: el.fontColor || '#FFFFFF',
                  fontFamily: el.fontFamily || 'sans-serif',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  textAlign: 'center', padding: 4,
                  userSelect: 'none',
                  textShadow: '0 1px 4px rgba(0,0,0,0.6)',
                  wordBreak: 'break-word', whiteSpace: 'pre-wrap',
                }}>
                  {el.content || 'Text'}
                </div>
              )}
            </Rnd>
          ))}
        </div>
      </div>
    </div>
  );
});

export default PosterCanvas;
